// Comprehensive Training Modules for Program Building Track

export const trainingModules = {
  // PHASE 1: Program Structure & Governance
  'module-1-1': {
    id: 'module-1-1',
    phaseId: 'phase-1',
    phaseName: 'Program Structure & Governance',
    title: 'Compliance Program Fundamentals',
    duration: 45,
    difficulty: 'beginner',
    videoUrl: 'https://www.youtube.com/embed/QO_jAFfQYso',
    learningObjectives: [
      'Understand the purpose and scope of a compliance program',
      'Define program objectives and governance structure',
      'Identify key roles and responsibilities',
      'Understand the importance of board and management oversight'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'What is a Compliance Program?',
        duration: 10,
        content: `A compliance program is a comprehensive system designed to ensure that an organization operates in accordance with applicable laws, regulations, and internal policies. Key components include:

CORE ELEMENTS:
• Risk Assessment - Identifying compliance risks specific to the organization
• Policy Development - Creating clear policies and procedures
• Training & Communication - Educating employees on compliance requirements
• Monitoring & Testing - Detecting and preventing compliance violations
• Escalation & Reporting - Managing violations and reporting to leadership
• Discipline - Responding to violations appropriately

PROGRAM OBJECTIVES:
1. Ensure regulatory compliance across all business units
2. Establish a culture of integrity and ethical conduct
3. Implement effective prevention and detection controls
4. Demonstrate commitment to compliance to regulators and stakeholders
5. Protect the organization from legal and reputational risks

WHY IT MATTERS:
Organizations without effective compliance programs face:
- Regulatory fines and penalties
- Criminal prosecution of executives
- Reputational damage and loss of customer trust
- Business restrictions or loss of license
- Decreased shareholder value

EFFECTIVE VS. INEFFECTIVE PROGRAMS:
Effective programs have:
✓ Clear leadership and governance
✓ Risk-based approach to controls
✓ Adequate resources and authority
✓ Regular training and communication
✓ Monitoring, testing, and remediation
✓ Strong enforcement of consequences

Ineffective programs lack:
✗ Clear leadership accountability
✗ Understanding of organizational risks
✗ Training or communication efforts
✗ Monitoring or testing procedures
✗ Process for remediating issues
✗ Consistent enforcement`
      },
      {
        id: 'lesson-2',
        title: 'Program Governance Structure',
        duration: 15,
        content: `Governance structures define how compliance decisions are made and who has authority. A well-designed governance structure includes:

GOVERNANCE FRAMEWORK:
1. Board of Directors/Audit Committee
   - Ultimate oversight responsibility
   - Approves compliance program
   - Receives compliance reports
   - Sets tone from the top

2. Chief Compliance Officer (CCO)
   - Day-to-day program management
   - Direct access to CEO/Board
   - Authority to escalate and enforce
   - Independent from business pressure

3. Compliance Committee
   - Cross-functional representation
   - Quarterly/monthly meetings
   - Reviews emerging risks
   - Coordinates compliance activities

4. Business Unit Compliance Officers
   - Front-line compliance responsibility
   - Local risk assessment and mitigation
   - Training and communication delivery
   - First-level incident detection

KEY GOVERNANCE PRINCIPLES:
✓ Clear Chain of Command
  - Define who reports to whom
  - Establish escalation paths
  - Empower decision-makers

✓ Independence
  - CCO reports to Board/CEO (not General Counsel/Finance)
  - Compliance not subordinate to business pressure
  - Protected from retaliation

✓ Authority
  - Access to all business areas and records
  - Power to investigate and recommend discipline
  - Resources to fulfill mandate

✓ Communication
  - Regular reporting to Board
  - Transparent escalation procedures
  - Clear communication of expectations

GOVERNANCE EXAMPLE - FINANCIAL SERVICES FIRM:
Board of Directors
    └── Chief Risk Officer (Board-level)
            └── Chief Compliance Officer
                    ├── AML Compliance Officer
                    ├── Sanctions Officer
                    ├── Regulatory Compliance Officer
                    └── Training Coordinator`
      },
      {
        id: 'lesson-3',
        title: 'Roles, Responsibilities & Accountability',
        duration: 20,
        content: `Clear role definition ensures accountability and effective execution. Each role has distinct responsibilities:

CHIEF COMPLIANCE OFFICER
Responsibilities:
• Develop and implement compliance program
• Report compliance status to Board
• Establish risk assessment process
• Oversee policy and procedure development
• Direct monitoring and testing activities
• Escalate and investigate violations
• Manage compliance budget and resources
• Provide compliance training

Accountability:
- Performance evaluated by Board/CEO
- Compensation linked to compliance effectiveness
- Protected from retaliation
- Authority to escalate issues

COMPLIANCE STAFF
Responsibilities:
• Execute daily compliance monitoring
• Conduct training and awareness programs
• Support investigations
• Maintain compliance documentation
• Coordinate with business units
• Test control effectiveness

Accountability:
- Report to CCO
- Performance based on monitoring quality
- Training completion and effectiveness

BUSINESS UNIT HEADS
Responsibilities:
• Embed compliance in operations
• Ensure staff compliance training
• Report compliance issues up chain
• Implement CCO recommendations
• Allocate resources for compliance
• Create compliance-focused culture

Accountability:
- Board/CEO holds them accountable
- Compensation may reflect compliance record
- Performance evaluations include compliance

BOARD OF DIRECTORS
Responsibilities:
• Set tone from the top
• Approve compliance program
• Oversee program effectiveness
• Allocate adequate resources
• Receive and review compliance reports
• Ensure enforcement of consequences

Accountability:
- Fiduciary duty to organization
- Regulatory expectations
- Shareholder/stakeholder confidence

RESPONSIBILITY MATRIX EXAMPLE:
Activity | CCO | Staff | Managers | Board
Risk Assessment | Oversee | Execute | Input | Approve
Policy Development | Approve | Draft | Review | Approve
Training | Plan | Deliver | Attend | None
Monitoring | Oversee | Execute | Support | Review
Investigation | Direct | Execute | Cooperate | Monitor
Discipline | Recommend | Execute | Support | Monitor`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the primary purpose of a compliance program?',
          options: [
            'To reduce compliance costs',
            'To ensure the organization operates in accordance with applicable laws and regulations',
            'To generate revenue for the organization',
            'To replace internal controls'
          ],
          correct: 1,
          explanation: 'The primary purpose is to ensure the organization operates in accordance with applicable laws, regulations, and internal policies. This protects the organization from legal, financial, and reputational risks.'
        },
        {
          id: 'q2',
          question: 'To whom should the Chief Compliance Officer report?',
          options: [
            'The General Counsel',
            'The CFO',
            'The CEO and/or Board of Directors',
            'The Audit Committee only'
          ],
          correct: 2,
          explanation: 'The CCO should report directly to the CEO and/or Board to ensure independence from business pressure and ensure compliance concerns are escalated appropriately.'
        },
        {
          id: 'q3',
          question: 'Which of the following is NOT a core element of an effective compliance program?',
          options: [
            'Risk Assessment',
            'Policy Development',
            'Marketing and Advertising',
            'Training and Communication'
          ],
          correct: 2,
          explanation: 'Marketing and advertising are not core elements of a compliance program. Core elements include risk assessment, policy development, training, monitoring, testing, and escalation procedures.'
        },
        {
          id: 'q4',
          question: 'Who has ultimate oversight responsibility for the compliance program?',
          options: [
            'The Chief Compliance Officer',
            'Business Unit Managers',
            'The Board of Directors',
            'External Auditors'
          ],
          correct: 2,
          explanation: 'The Board of Directors has ultimate oversight responsibility. They approve the program, receive regular reports, and ensure adequate resources are allocated.'
        },
        {
          id: 'q5',
          question: 'What is a key principle of effective governance?',
          options: [
            'Compliance should report to the General Counsel',
            'CCO independence from business pressure',
            'Compliance monitoring should be limited to finance department',
            'Board should not be involved in compliance oversight'
          ],
          correct: 1,
          explanation: 'CCO independence from business pressure is a key governance principle. This ensures compliance decisions are made based on risk, not business convenience.'
        }
      ],
      passingScore: 80
    }
  },

  'module-1-2': {
    id: 'module-1-2',
    phaseId: 'phase-1',
    phaseName: 'Program Structure & Governance',
    title: 'Leadership and Oversight',
    duration: 30,
    difficulty: 'beginner',
    learningObjectives: [
      'Understand board and executive oversight responsibilities',
      'Learn how to communicate compliance status to leadership',
      'Understand escalation procedures and decision-making authority',
      'Recognize the importance of board-level compliance attention'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Board Oversight & Responsibilities',
        duration: 12,
        content: `Board of Directors' Role in Compliance

BOARD RESPONSIBILITIES:
1. Governance Oversight
   - Approve compliance program design
   - Monitor program effectiveness
   - Allocate adequate resources
   - Ensure appropriate authority and reporting

2. Strategic Guidance
   - Set compliance expectations
   - Establish risk tolerance
   - Define organizational values
   - Create "tone from the top"

3. Compliance Reporting
   - Receive regular compliance reports (at minimum quarterly)
   - Review compliance metrics and KPIs
   - Assess emerging risks
   - Evaluate remediation efforts

4. Accountability & Enforcement
   - Hold executives accountable for compliance
   - Link executive compensation to compliance
   - Approve consequences for violations
   - Support compliance investigations

5. Resource Allocation
   - Approve compliance budget
   - Authorize technology investments
   - Support staffing needs
   - Fund training programs

BOARD ENGAGEMENT BEST PRACTICES:
✓ Monthly or Quarterly Compliance Updates
  - Key metrics and trends
  - Emerging risks and issues
  - Regulatory developments
  - Remediation progress

✓ Annual Deep Dives
  - Comprehensive program review
  - Strategy and resource planning
  - Regulatory examination results
  - Third-party assessment findings

✓ Prompt Escalation of Critical Issues
  - Serious violations or misconduct
  - Regulatory enforcement actions
  - Significant control failures
  - Reputational risks

AUDIT COMMITTEE VS FULL BOARD:
Many organizations assign compliance oversight to the Audit Committee:
- More frequent meetings (monthly vs quarterly)
- Detailed compliance discussion
- Audit Committee reports to full Board
- Full Board approves major decisions

RED FLAGS - INSUFFICIENT BOARD ENGAGEMENT:
✗ CCO reports quarterly or less frequently
✗ Compliance issues surprise the Board
✗ No compliance discussion during executive compensation review
✗ Board cuts compliance budget without understanding impact
✗ Compliance concerns are ignored or dismissed`
      },
      {
        id: 'lesson-2',
        title: 'Escalation Procedures',
        duration: 10,
        content: `Effective Escalation Ensures Issues Reach Right Decision-Makers

ESCALATION FRAMEWORK:
Level 1 - Operational Issues
- Day-to-day violations detected through monitoring
- Handled by compliance officer and business manager
- Resolution within 10-15 business days
- Documentation required

Level 2 - Significant Issues
- Material violations or patterns
- Involves multiple business units or departments
- Requires business unit head approval
- Report to CCO
- Resolution within 30 days
- Executive summary to audit committee

Level 3 - Critical Issues
- Potential criminal activity
- Major regulatory violation
- Significant financial impact
- Executive/Board misconduct
- Report immediately to Board
- Notification to counsel and external parties as needed

ESCALATION DECISION FACTORS:
Ask: Does this issue involve...
- Potential legal/regulatory violation?
- Senior management misconduct?
- Significant financial exposure?
- Customer/vendor/regulatory interest?
- Media/public relations concern?
- Fraud or criminal activity?

If YES to any → Escalate to next level

ESCALATION TIMING:
Critical Issues: Same business day notification
Significant Issues: Within 2-3 business days
Routine Issues: Monthly compliance report

ESCALATION DOCUMENTATION:
Include:
- Clear description of issue
- Individuals involved
- Potential impact/exposure
- Recommended action
- Timeline for resolution
- Contact information for follow-up

ESCALATION CHANNELS:
Primary: CCO (for most issues)
Bypass: Board Audit Committee (for CCO issues)
Legal Issues: General Counsel (for potential litigation)
Criminal Issues: Board + External Counsel + External Auditors`
      },
      {
        id: 'lesson-3',
        title: 'Communication with Leadership',
        duration: 8,
        content: `Effective communication of compliance status builds Board confidence and support.

KEY MESSAGES FOR BOARD:
1. Program Status
   - Overall effectiveness assessment
   - Key metrics and trends
   - Emerging risks

2. Resource Needs
   - Technology investments needed
   - Staffing requirements
   - Budget requirements
   - Timeline and expected ROI

3. Regulatory Developments
   - New or changed regulations
   - Examination/audit results
   - Industry trends
   - Required responses

4. Critical Issues
   - Significant violations discovered
   - Regulatory actions
   - Media exposure
   - Recommended remediation

BOARD REPORTING FORMAT:
Executive Summary (1 page)
- Overall program health: Green/Yellow/Red
- Key issues and status
- Resource needs

Detailed Report (5-10 pages)
- Program metrics and trends
- Specific issues and remediation
- Regulatory updates
- Upcoming initiatives

Supporting Materials
- Detailed incident reports (confidential)
- Trend analysis
- Regulatory citations
- Risk assessment results

TONE & APPROACH:
✓ Objective and fact-based
✓ Forward-looking recommendations
✓ Clear action items and owners
✓ Realistic timelines
✓ Resource transparency
✗ Minimize jargon
✗ Avoid blame/finger-pointing
✗ Don't sugarcoat issues
✗ Don't present as "all under control" if problems exist`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is a key board responsibility for compliance?',
          options: [
            'Conducting daily monitoring activities',
            'Approving the compliance program and monitoring effectiveness',
            'Drafting compliance policies',
            'Investigating every violation personally'
          ],
          correct: 1,
          explanation: 'The board approves the compliance program and monitors its effectiveness. Day-to-day activities are delegated to management and compliance staff.'
        },
        {
          id: 'q2',
          question: 'How frequently should the board receive compliance updates?',
          options: [
            'Annually',
            'When issues arise',
            'At minimum quarterly, with more frequent updates during crises',
            'Never - Board should not be involved in compliance'
          ],
          correct: 2,
          explanation: 'The board should receive compliance updates at minimum quarterly, or more frequently when significant issues arise. Regular reporting ensures board oversight.'
        },
        {
          id: 'q3',
          question: 'What should trigger immediate escalation to the board?',
          options: [
            'Employee accidentally accessing wrong file',
            'Potential criminal activity or major regulatory violation',
            'Minor policy deviation by single employee',
            'Routine control failures'
          ],
          correct: 1,
          explanation: 'Critical issues like potential criminal activity, major regulatory violations, or significant financial impact should be escalated to the board immediately.'
        }
      ],
      passingScore: 80
    }
  },

  'module-1-3': {
    id: 'module-1-3',
    phaseId: 'phase-1',
    phaseName: 'Program Structure & Governance',
    title: 'Organizational Alignment',
    duration: 40,
    difficulty: 'intermediate',
    learningObjectives: [
      'Understand how to align compliance with organizational structure',
      'Learn stakeholder engagement strategies',
      'Develop cross-functional collaboration approaches',
      'Create change management strategy for program implementation'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Aligning Compliance with Organization',
        duration: 15,
        content: `Effective compliance requires understanding and working within organizational structure.

ORGANIZATION STRUCTURES:
1. Functional Structure
   - Organized by function (Finance, Operations, HR, Compliance)
   - Compliance often in Finance or Legal
   - Challenge: Compliance may be subordinate to other functions
   - Solution: Elevate CCO to executive level

2. Business Unit Structure
   - Organized by product/geography
   - Compliance embedded in each business unit
   - Challenge: Inconsistent standards across units
   - Solution: Centralized compliance policy with local execution

3. Matrix Structure
   - Dual reporting relationships
   - CCO reports to Board + CFO (or CEO + Board)
   - Challenge: Competing priorities
   - Solution: Clear governance about compliance authority

4. Hybrid Structure
   - Combination of functional and business unit
   - Centralized compliance sets policy, business units execute
   - Most common in large organizations
   - Best practice: Clear separation of responsibilities

COMPLIANCE POSITIONING:
✓ Optimal Positioning
  - Reports to Board Audit Committee
  - Direct access to CEO
  - Independent from business pressure
  - Authority over all business units
  - Adequate budget and resources

Suboptimal Positioning
  - Reports to CFO/General Counsel
  - Must defer to business units
  - Limited authority to enforce
  - Budget subject to business pressures

ORGANIZATION MAPPING EXERCISE:
Step 1: Map current org chart
Step 2: Identify CCO reporting line
Step 3: Assess compliance independence
Step 4: Identify business unit liaisons
Step 5: Define escalation paths

KEY POSITIONS TO CREATE:
1. Chief Compliance Officer (new or elevated role)
2. Risk & Controls Officer
3. Compliance Monitoring Officer
4. Training & Communications Officer
5. Business Unit Compliance Liaisons`
      },
      {
        id: 'lesson-2',
        title: 'Stakeholder Engagement',
        duration: 15,
        content: `Engaging stakeholders ensures program support and buy-in.

STAKEHOLDER IDENTIFICATION:
Primary Stakeholders (directly affected):
- Board of Directors
- Executive Management
- Business Unit Heads
- Compliance Team
- Front-line Staff

Secondary Stakeholders (indirectly affected):
- Audit/Finance Functions
- Legal/Regulatory Affairs
- Human Resources
- External Auditors
- Regulators

ENGAGEMENT STRATEGY BY STAKEHOLDER:

Executive Management
- Objective: Secure leadership support and resources
- Approach: Executive briefings on program benefits
- Frequency: Quarterly updates
- Key Messages: Risk mitigation, regulatory compliance, reputation

Business Unit Heads
- Objective: Integrate compliance into operations
- Approach: Training and joint planning sessions
- Frequency: Monthly meetings
- Key Messages: Ownership in execution, performance metrics

Front-line Staff
- Objective: Build compliance culture
- Approach: Training, communication, feedback loops
- Frequency: Ongoing
- Key Messages: Ethical conduct, reporting channels, protection from retaliation

ENGAGEMENT ACTIVITIES:
Phase 1: Discovery & Awareness
- Communicate program purpose
- Explain why compliance matters
- Acknowledge concerns
- Timeline: 2-4 weeks

Phase 2: Buy-in Building
- Executive forums
- Q&A sessions
- Pilot programs in select business units
- Timeline: 4-6 weeks

Phase 3: Active Participation
- Joint planning sessions
- Resource commitment
- Training and communication
- Timeline: Ongoing

RESISTANCE MANAGEMENT:
Common Objection: "Compliance slows us down"
Response: Effective compliance enables sustainable business growth

Common Objection: "This is compliance department's job"
Response: Compliance is everyone's responsibility; compliance department enables and supports

Common Objection: "We don't have budget"
Response: Cost of non-compliance far exceeds cost of effective program`
      },
      {
        id: 'lesson-3',
        title: 'Building Compliance Culture',
        duration: 10,
        content: `A strong compliance culture is the foundation of an effective program.

CULTURE CHARACTERISTICS:
Strong Compliance Culture
✓ Employees understand compliance expectations
✓ Ethical conduct is valued and rewarded
✓ Violations are identified and escalated
✓ Leaders model compliance behaviors
✓ Employees feel protected for reporting issues
✓ Consequences for violations are consistent

Weak Compliance Culture
✗ Employees unsure of compliance expectations
✗ "Compliance as obstacle" mentality
✗ Issues hidden or unreported
✗ Leaders overlook violations
✗ Employees fear retaliation for reporting
✗ Consequences for violations inconsistent

CULTURE BUILDING INITIATIVES:
1. Leadership Example
   - Leaders model ethical behavior
   - Executives comply with policies
   - Violations addressed regardless of seniority
   - Compliance included in performance evaluations

2. Clear Communication
   - Regular compliance messages
   - Training on expectations
   - Open communication about risks
   - Recognition of good compliance practices

3. Safe Reporting
   - Multiple reporting channels (hotline, email, in-person)
   - Anonymous reporting options
   - Non-retaliation policy
   - Regular feedback on status

4. Recognition & Incentives
   - Recognize compliance "champions"
   - Include compliance in performance reviews
   - Link compensation to compliance
   - Celebrate compliance successes

5. Continuous Improvement
   - Ask for employee feedback
   - Adjust policies based on feedback
   - Share remediation lessons
   - Demonstrate that concerns are addressed

TONE FROM THE TOP:
The most important factor in building culture is leadership commitment:
- CEO publicly supports compliance
- Board allocates resources
- Executives comply with policies
- Violations addressed consistently
- Compliance discussed in executive meetings`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What organizational structure is optimal for compliance independence?',
          options: [
            'CCO reports to CFO',
            'CCO reports to General Counsel',
            'CCO reports to Board with direct CEO access',
            'CCO is part of Finance Department'
          ],
          correct: 2,
          explanation: 'The CCO should report to the Board (or Audit Committee) with direct CEO access to ensure independence from business pressure and financial considerations.'
        },
        {
          id: 'q2',
          question: 'What is the primary purpose of stakeholder engagement?',
          options: [
            'To bypass business processes',
            'To build support and buy-in for the compliance program',
            'To reduce compliance costs',
            'To eliminate the need for training'
          ],
          correct: 1,
          explanation: 'Stakeholder engagement builds support and buy-in, ensuring the compliance program is integrated into business operations effectively.'
        },
        {
          id: 'q3',
          question: 'Which is a characteristic of a strong compliance culture?',
          options: [
            'Employees fear reporting violations',
            'Compliance is only finance department responsibility',
            'Ethical conduct is valued and rewarded',
            'Violations are hidden to protect the organization'
          ],
          correct: 2,
          explanation: 'A strong compliance culture values ethical conduct, rewards compliance, and encourages reporting of violations without fear of retaliation.'
        }
      ],
      passingScore: 80
    }
  },

  // PHASE 2: Risk Assessment & Control Design
  'module-2-1': {
    id: 'module-2-1',
    phaseId: 'phase-2',
    phaseName: 'Risk Assessment & Control Design',
    title: 'Risk Assessment Methodologies',
    duration: 60,
    difficulty: 'intermediate',
    learningObjectives: [
      'Conduct comprehensive compliance risk assessments',
      'Identify and categorize compliance risks',
      'Develop risk scoring methodology',
      'Create risk heat maps and prioritization matrices'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Risk Assessment Framework',
        duration: 20,
        content: `A systematic approach to identifying and assessing compliance risks.

RISK ASSESSMENT PROCESS:
Step 1: Define Scope & Objectives (Week 1)
- Determine which business areas to assess
- Identify applicable regulations
- Define risk appetite
- Identify assessment team

Step 2: Risk Identification (Weeks 2-3)
- Conduct interviews with business leaders
- Document processes and controls
- Review past violations
- Identify new business activities

Step 3: Risk Evaluation (Weeks 4-5)
- Assess likelihood of violation
- Evaluate potential impact
- Score each risk
- Identify compensating controls

Step 4: Risk Prioritization (Week 6)
- Rank risks by severity
- Allocate resources
- Develop mitigation plans
- Create action plan

RISK CATEGORIES:
Regulatory/Compliance Risk
- Non-compliance with laws/regulations
- Examples: AML, Sanctions, KYC, Reporting

Operational Risk
- Failures in systems or processes
- Examples: Failed controls, data breaches, system downtime

Strategic Risk
- Failures in strategy or governance
- Examples: Poor oversight, inadequate resources, weak culture

Reputational Risk
- Damage to organizational reputation
- Examples: Public violations, media exposure, customer loss

RISK IDENTIFICATION TECHNIQUES:
1. Workshops
   - Gather business leaders and subject matter experts
   - Brainstorm potential risks
   - Document emerging risks
   - Efficient for large organizations

2. Interviews
   - One-on-one discussions
   - Deep dive into specific areas
   - Understand context and reasoning
   - Time-intensive but thorough

3. Process Mapping
   - Document current processes
   - Identify control gaps
   - Test for single points of failure
   - Valuable for operational areas

4. Regulatory Analysis
   - Review applicable regulations
   - Map regulations to processes
   - Identify compliance requirements
   - Prioritize high-impact regulations

5. Benchmarking
   - Review industry standards
   - Compare to peer practices
   - Identify best practices
   - Useful for new compliance areas`
      },
      {
        id: 'lesson-2',
        title: 'Risk Scoring & Prioritization',
        duration: 25,
        content: `Quantifying risk enables objective prioritization and resource allocation.

RISK SCORING METHODOLOGY:
Inherent Risk = Likelihood × Impact
(Risk without any controls)

Likelihood Scale (1-5):
1 = Very Unlikely (< 5% chance annually)
2 = Unlikely (5-20% chance annually)
3 = Possible (20-50% chance annually)
4 = Likely (50-80% chance annually)
5 = Very Likely (> 80% chance annually)

Impact Scale (1-5):
1 = Minor (< $500K impact or limited regulatory notice)
2 = Significant ($500K - $2M impact or moderate notice)
3 = Substantial ($2M - $10M impact or regulatory action)
4 = Major ($10M - $50M impact or license restriction)
5 = Catastrophic (> $50M impact or license revocation)

RISK HEAT MAP:
             Impact
             1    2    3    4    5
Likelihood
5            5   10   15   20   25
4            4    8   12   16   20
3            3    6    9   12   15
2            2    4    6    8   10
1            1    2    3    4    5

Color Coding:
Red (15-25): Critical - Immediate action required
Orange (9-14): High - Significant action needed
Yellow (5-8): Medium - Mitigation required
Green (1-4): Low - Monitor and review

RESIDUAL RISK:
After implementing controls, recalculate:
Residual Risk = Inherent Risk - Control Effectiveness

Control Effectiveness (0-100%):
- Preventive Controls: Reduce likelihood
- Detective Controls: Reduce impact through early detection

RISK PRIORITIZATION CRITERIA:
1. Risk Score (primary)
2. Strategic Importance
3. Resource Availability
4. Timeline for Mitigation
5. Regulatory Attention

RISK ASSESSMENT EXAMPLE:
Risk: Customer not properly screened at account opening
Likelihood: 4 (Current process has 10% failure rate)
Impact: 4 (Regulatory penalties, license at risk)
Inherent Risk Score: 16 (HIGH)

Current Controls:
- Manual name checking (80% effective)
- Manager review (70% effective)
- Spot checking (40% effective)

Residual Risk: 16 × 0.2 = 3.2 (LOW after controls)

Mitigation: Implement automated screening system`
      },
      {
        id: 'lesson-3',
        title: 'Documentation & Reporting',
        duration: 15,
        content: `Thorough documentation ensures consistency and provides audit trail.

RISK ASSESSMENT DOCUMENTATION:
1. Risk Register
   - List all identified risks
   - Inherent and residual risk scores
   - Current controls
   - Mitigation plans
   - Responsible parties
   - Target completion dates

2. Risk Narrative
   - Description of each risk
   - How risk could occur
   - Potential impact
   - Current controls
   - Control effectiveness
   - Recommended mitigations

3. Process Maps
   - Flowcharts of key processes
   - Identify control points
   - Show data flows
   - Highlight risks
   - Document compensating controls

4. Supporting Documentation
   - Interview notes
   - Regulatory requirements matrix
   - Policies and procedures
   - Test results
   - Regulatory guidance

RISK ASSESSMENT REPORT:
Executive Summary (1 page)
- Overall risk profile
- Top 5 risks
- Key recommendations
- Resource needs

Detailed Findings (10-20 pages)
- Risk assessment methodology
- Detailed risk descriptions
- Risk scoring rationale
- Prioritized action plans
- Timeline and resource requirements

Appendices
- Risk register
- Process maps
- Regulatory matrix
- Interview summaries
- Control documentation

STAKEHOLDER COMMUNICATION:
Board/Executive Presentation
- Focus on top risks
- Emphasize impact
- Highlight resource needs
- Recommend mitigation
- Timeline for implementation

Business Unit Communication
- Share relevant risks
- Explain mitigation needs
- Clarify responsibilities
- Provide training needs
- Solicit feedback

PUBLIC/REGULATORY COMMUNICATION
- Demonstrates compliance program
- Shows risk awareness
- Highlights mitigation efforts
- Demonstrates risk management
- Can be required in examinations`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is Inherent Risk?',
          options: [
            'Risk after implementing controls',
            'Risk without any controls or mitigations',
            'Risk that cannot be addressed',
            'Risk of control failure'
          ],
          correct: 1,
          explanation: 'Inherent Risk is the risk without any controls or mitigations. It represents the organization\'s natural exposure to a risk area.'
        },
        {
          id: 'q2',
          question: 'On a 1-5 scale, what likelihood rating means 50-80% probability annually?',
          options: [
            '2 = Unlikely',
            '3 = Possible',
            '4 = Likely',
            '5 = Very Likely'
          ],
          correct: 2,
          explanation: 'A likelihood rating of 4 represents a "Likely" risk with 50-80% probability of occurring annually.'
        },
        {
          id: 'q3',
          question: 'What risk score range indicates critical risk requiring immediate action?',
          options: [
            '1-4',
            '5-8',
            '9-14',
            '15-25'
          ],
          correct: 3,
          explanation: 'A risk score of 15-25 is in the critical/red zone, requiring immediate action and resource allocation.'
        },
        {
          id: 'q4',
          question: 'Which technique is best for understanding specific business processes in detail?',
          options: [
            'Workshops',
            'Regulatory analysis',
            'Process mapping and interviews',
            'Benchmarking'
          ],
          correct: 2,
          explanation: 'Process mapping and interviews allow for detailed understanding of how specific processes work and where risks exist.'
        }
      ],
      passingScore: 80
    }
  },

  // Phase 2 Module 2 - Control Design
  'module-2-2': {
    id: 'module-2-2',
    phaseId: 'phase-2',
    phaseName: 'Risk Assessment & Control Design',
    title: 'Control Design Principles',
    duration: 45,
    difficulty: 'intermediate',
    learningObjectives: [
      'Understand control types and design principles',
      'Design effective preventive and detective controls',
      'Implement control testing framework',
      'Measure control effectiveness'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Control Types & Design',
        duration: 20,
        content: `Different control types serve different purposes in managing risk.

CONTROL TYPES:

1. PREVENTIVE CONTROLS
Purpose: Stop violations before they occur
Examples:
- System controls (cannot approve without supervisor)
- Process controls (mandatory escalation for exceptions)
- Authorization limits (transaction amounts based on role)
- Segregation of duties (different people perform conflicting tasks)
Effectiveness: Most cost-effective; prevents violations

2. DETECTIVE CONTROLS
Purpose: Identify violations after they occur
Examples:
- Monitoring reports (suspicious transactions)
- Exception reports (transactions outside normal parameters)
- Sampling and testing (verify controls working)
- Reconciliations (verify completeness and accuracy)
Effectiveness: Catches issues for escalation and correction

3. CORRECTIVE CONTROLS
Purpose: Fix violations once detected
Examples:
- Escalation procedures (notify appropriate parties)
- Remediation processes (fix identified issues)
- Reversal procedures (undo unauthorized transactions)
- Retraining (address knowledge gaps)
Effectiveness: Limits damage; ensures compliance restored

CONTROL DESIGN BEST PRACTICES:
✓ Clear Objective
  - What specific risk does this control address?
  - What outcome should the control achieve?
  - How will we know if control is effective?

✓ Efficient Execution
  - Automated where possible
  - Minimal manual intervention
  - Clear procedures
  - Realistic resource requirements

✓ Appropriate Level
  - Control strength matches risk level
  - Resource intensity matches risk severity
  - Balances cost vs. benefit
  - Not overly burdensome

✓ Independence
  - Person executing control != transaction creator
  - Different data sources for verification
  - Objective criteria (not subjective)
  - Evidence-based assessment

✓ Documentation
  - Clear procedures
  - Decision-making criteria
  - Evidence of control execution
  - Frequency and timing

CONTROL DESIGN EXAMPLE:
Risk: Customer not properly screened at account opening

Option 1: Preventive Control
- Automated system requires name check before account opens
- System blocks if potential match found
- Strengths: Stops issues before they occur
- Weaknesses: May block legitimate customers; requires system change

Option 2: Detective Control
- Manual review of new accounts within 24 hours
- Compare to watchlists using software
- Escalate any matches to compliance
- Strengths: Lower cost; requires no system change
- Weaknesses: Issues not prevented; may require reversal

Recommended Approach: Combination
- Automated initial check (preventive)
- Automated watchlist screening (detective)
- Manual exception review (detective)
- Provides layered protection`
      },
      {
        id: 'lesson-2',
        title: 'Control Matrix & Testing',
        duration: 15,
        content: `Documenting controls in a matrix enables tracking and testing.

CONTROL MATRIX COMPONENTS:
Risk ID | Risk Description | Control ID | Control | Type | Owner | Frequency | Evidence | Testing
RA-001 | Customer not screened | C-001 | System blocks account opening without name check | Prev | Operations | Real-time | System log | Quarterly sample

CRITICAL INFORMATION IN MATRIX:
1. Risk Mapping
   - Which risk does each control address?
   - Does each risk have adequate controls?
   - Are there overlapping controls?

2. Control Ownership
   - Who executes the control daily?
   - Who monitors effectiveness?
   - Who has authority to waive?
   - Who escalates exceptions?

3. Control Frequency
   - Real-time (automated, continuous)
   - Daily (manual, every business day)
   - Weekly (scheduled weekly)
   - Monthly (scheduled monthly)
   - Quarterly (scheduled quarterly)
   - Annual (scheduled yearly)

4. Evidence & Documentation
   - System reports
   - Signed sign-offs
   - Meeting minutes
   - Email confirmations
   - Database records

5. Testing Methodology
   - Automated testing (computer programs)
   - Sample testing (test subset of transactions)
   - Full testing (test 100% of transactions)
   - Observation (watch process being executed)

CONTROL TESTING FRAMEWORK:
1. Design Testing
   - Does the control exist?
   - Is control documented?
   - Is control appropriately designed?
   - Will control address the risk?

2. Operating Effectiveness Testing
   - Is the control being executed?
   - Is it executed consistently?
   - Does it achieve its objective?
   - What is the failure rate?

3. Results Documentation
   - What % of items tested passed?
   - What issues were identified?
   - What is the root cause?
   - What remediation is needed?

CONTROL TESTING EXAMPLE:
Risk: Customer not screened at onboarding
Control: Automated system check against watchlists

Design Test:
- Is system check configured? YES
- Does system prevent account opening on match? YES
- Is documentation adequate? YES
- Control Design: EFFECTIVE

Operating Test (Sample 50 new accounts from January):
- How many accounts had required check? 48 of 50 (96%)
- How many potential matches escalated? 2 of 50 (4%)
- How many escalations properly resolved? 2 of 2 (100%)
- Control Operating Effectiveness: 96%`
      },
      {
        id: 'lesson-3',
        title: 'Measuring Control Effectiveness',
        duration: 10,
        content: `Control effectiveness is measured through testing and metrics.

CONTROL EFFECTIVENESS METRICS:
1. Compliance Rate
   - % of control executions that were compliant
   - Formula: Compliant executions / Total executions
   - Example: 48 of 50 accounts screened = 96% compliance

2. Timeliness
   - % of controls executed on schedule
   - Formula: On-time executions / Total executions
   - Example: 45 of 50 monthly reports delivered on time = 90% timeliness

3. Exception Rate
   - % of items failing control
   - Formula: Failed items / Total items
   - Example: 2 of 50 accounts had issues = 4% exception rate

4. Remediation Rate
   - % of exceptions properly remediated
   - Formula: Remediated exceptions / Total exceptions
   - Example: 2 of 2 exceptions escalated = 100% remediation

5. Cost-Effectiveness
   - Cost of control vs. value protected
   - High cost + High effectiveness = Good investment
   - High cost + Low effectiveness = Review for efficiency
   - Low cost + High effectiveness = Best practice

CONTROL EFFECTIVENESS ASSESSMENT:
Green (90-100% effective)
- Control is operating as designed
- Exceptions are rare and properly handled
- No improvements needed at this time

Yellow (75-89% effective)
- Control is mostly effective
- Some exceptions occurring
- Improvements recommended
- Monitor for increasing issues

Red (< 75% effective)
- Control is not operating effectively
- Too many exceptions
- Immediate improvement required
- Consider design changes

CONTINUOUS IMPROVEMENT:
1. Analyze failures
   - Why did control fail?
   - Was it process issue or design issue?
   - Could issue have been prevented?

2. Root cause analysis
   - What was the underlying cause?
   - Is this recurring issue?
   - What would prevent recurrence?

3. Improvement actions
   - Adjust process or procedures
   - Provide additional training
   - Implement system enhancements
   - Revise control design

4. Monitor results
   - Track improved metrics
   - Test again after changes
   - Verify sustained improvement
   - Document lessons learned`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the purpose of a preventive control?',
          options: [
            'To detect violations after they occur',
            'To stop violations before they occur',
            'To fix violations once they are discovered',
            'To eliminate the need for testing'
          ],
          correct: 1,
          explanation: 'Preventive controls stop violations before they occur, making them the most cost-effective type of control.'
        },
        {
          id: 'q2',
          question: 'Which is NOT a component of a control matrix?',
          options: [
            'Risk ID and description',
            'Control type and frequency',
            'Control owner and evidence',
            'Employee salary and benefits'
          ],
          correct: 3,
          explanation: 'A control matrix documents controls and their attributes, not employee compensation information.'
        },
        {
          id: 'q3',
          question: 'What is control effectiveness of 96% interpreted as?',
          options: [
            'Red - immediate improvement required',
            'Yellow - improvements recommended',
            'Green - control operating as designed',
            'Control has failed'
          ],
          correct: 2,
          explanation: 'A 96% effectiveness rate (90-100% range) is Green, meaning the control is operating as designed with rare exceptions.'
        }
      ],
      passingScore: 80
    }
  },

  // Phase 2 Module 3 - KRI and Monitoring
  'module-2-3': {
    id: 'module-2-3',
    phaseId: 'phase-2',
    phaseName: 'Risk Assessment & Control Design',
    title: 'Key Risk Indicators & Monitoring',
    duration: 40,
    difficulty: 'intermediate',
    learningObjectives: [
      'Develop Key Risk Indicators (KRIs)',
      'Design monitoring dashboards',
      'Establish escalation thresholds',
      'Create automated monitoring processes'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Developing Key Risk Indicators',
        duration: 15,
        content: `KRIs provide early warning signals of emerging risks.

WHAT ARE KRIS?
Key Risk Indicators (KRIs) are metrics that provide early warning of increasing risk exposure. They help identify when risks are growing before violations occur.

KRI CHARACTERISTICS:
✓ Forward-looking (predicts future violations)
✓ Measurable (quantifiable, not subjective)
✓ Actionable (triggers specific response)
✓ Timely (collected/updated regularly)
✓ Risk-correlated (directly related to risk)
✓ Understandable (clear interpretation)

KRI EXAMPLES BY AREA:
AML/CFT Compliance
- % of new customers with zero transaction volume
- % of transactions with no documentation
- % of high-risk customers monitored
- Monitoring system alert rate
- Customer screening rejection rate

Sanctions Compliance
- Transaction volume to sanctioned jurisdictions
- Customer screening match rate
- Overdue sanctions screening
- Screen effectiveness rate

Operational Risk
- System uptime percentage
- Unresolved exception backlog
- Staff turnover in compliance
- Overtime hours for staff
- Training completion rates

KRI THRESHOLDS:
Normal Range (Green)
- 0-50% of new customers with zero transactions
- Indicates normal customer acquisition

Warning Level (Yellow)
- 51-75% of new customers with zero transactions
- Indicates unusual pattern; investigate cause

Critical Level (Red)
- >75% of new customers with zero transactions
- Indicates possible structuring or account manipulation; escalate immediately

KRI MEASUREMENT:
Frequency:
- Daily: High-risk areas, automated transactions
- Weekly: Transaction monitoring, new account activity
- Monthly: Customer quality, staffing metrics
- Quarterly: Strategic metrics, forward-looking indicators

Collection Method:
- Automated system reports (preferred)
- Manual calculations from system data
- Surveys or assessments
- Observation or sampling

Reporting:
- Daily to compliance management
- Weekly to business unit heads
- Monthly to executive management
- Quarterly to Board`
      },
      {
        id: 'lesson-2',
        title: 'Monitoring Dashboards',
        duration: 15,
        content: `Visual presentation of KRIs enables quick understanding of risk status.

DASHBOARD DESIGN PRINCIPLES:
✓ At-a-Glance Insight
  - Quickly understand risk status
  - Use color coding (Red/Yellow/Green)
  - Minimal text, maximum clarity
  - Focus on critical metrics only

✓ Drill-Down Capability
  - Executive summary with charts
  - Ability to see underlying details
  - Drill from dashboard to data
  - Answer "why" questions

✓ Trend Analysis
  - Show historical performance
  - Identify increasing/decreasing trends
  - Compare to benchmarks
  - Forecast future issues

✓ Actionability
  - Clear interpretation
  - Recommended actions
  - Responsible parties
  - Timeline for action

DASHBOARD ELEMENTS:
1. Executive Summary (Top Page)
   - Overall risk rating
   - Top 5 KRIs by severity
   - Trend vs. previous period
   - Major issues and status

2. Detailed Dashboards by Area
   - AML/CFT Dashboard
   - Sanctions Monitoring
   - Operational Risk
   - Training & Awareness
   - Reporting Accuracy

3. Supporting Data
   - KRI charts and trends
   - Detail transaction lists
   - Root cause analysis
   - Remediation tracking

DASHBOARD DISTRIBUTION:
Board/Executive Dashboard (Monthly)
- High-level metrics
- Strategic trends
- Major issues
- Resource impacts

Compliance Management Dashboard (Weekly)
- Detailed KRIs
- Exception lists
- Escalation tracking
- Remediation status

Business Unit Dashboard (Daily)
- Performance vs. thresholds
- Specific exceptions
- Training completion
- Local issues

TECHNOLOGY CONSIDERATIONS:
Ideal Dashboard System:
- Automated data collection
- Real-time or near-real-time updates
- Customizable views by user role
- Mobile accessibility
- Historical trend analysis
- Export capability for reporting

Tools:
- Compliance software (vendors provide dashboards)
- Business intelligence tools
- Data warehouse solutions
- Excel/custom reports (less sophisticated)`
      },
      {
        id: 'lesson-3',
        title: 'Escalation & Response',
        duration: 10,
        content: `Clear escalation procedures ensure timely response to emerging risks.

ESCALATION THRESHOLDS:
Define specific trigger points for escalation:

Green (Normal)
- KRI within normal range
- No action required
- Continue monitoring

Yellow (Warning)
- KRI above normal but below critical
- Investigate cause
- Develop action plan
- Escalate to business unit head
- Timeline: 3-5 business days

Red (Critical)
- KRI at critical level
- Immediate investigation required
- Urgent action plan needed
- Escalate to CCO/Executive
- Timeline: Same business day

RESPONSE PROCEDURE:
1. Alert/Notification
   - Automated alert when threshold exceeded
   - Notification to responsible manager
   - Escalation to compliance if not addressed

2. Investigation
   - Determine root cause
   - Assess severity
   - Identify affected transactions
   - Estimate impact

3. Action Planning
   - Develop corrective actions
   - Assign responsibility
   - Set timeline for completion
   - Identify resource needs

4. Implementation
   - Execute corrective actions
   - Communicate to business units
   - Provide training if needed
   - Monitor results

5. Verification
   - Test that issue is resolved
   - Verify KRI returned to normal
   - Document resolution
   - Report to leadership

ESCALATION EXAMPLE:
AML Monitoring - Alert Volume Spike

Green (Normal): 50-100 alerts/day
Yellow (Warning): 101-200 alerts/day
Red (Critical): >200 alerts/day

Alert Volume on Tuesday: 245 alerts
Status: RED - CRITICAL

Immediate Response (Same Day):
- Alert compliance management
- Investigate root cause
- Determine if system issue or business issue
- Take emergency measures if needed
- Brief CCO

Investigation Results:
Root Cause: System malfunction creating duplicate alerts
Fix: System vendor identified and corrected issue
Result: Alert volume normalized to 95/day by Wednesday

Resolution Documentation:
- Document issue and resolution
- Update system testing procedures
- Identify lessons learned
- Report to board/audit committee`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is a Key Risk Indicator (KRI)?',
          options: [
            'A metric that measures past violations',
            'An early warning signal of increasing risk exposure',
            'A list of policies and procedures',
            'A regulatory requirement'
          ],
          correct: 1,
          explanation: 'A KRI is a forward-looking metric that provides early warning of increasing risk exposure before violations occur.'
        },
        {
          id: 'q2',
          question: 'What should trigger a Yellow (Warning) alert?',
          options: [
            'KRI within normal range',
            'KRI above normal but below critical level',
            'KRI at critical level',
            'Monthly reporting only'
          ],
          correct: 1,
          explanation: 'A Yellow alert indicates the KRI is above normal but below the critical threshold, warranting investigation and action planning.'
        },
        {
          id: 'q3',
          question: 'How often should Board-level risk dashboards be updated?',
          options: [
            'Daily',
            'Weekly',
            'Monthly',
            'Quarterly'
          ],
          correct: 2,
          explanation: 'Board dashboards are typically updated monthly, showing high-level metrics and strategic trends.'
        }
      ],
      passingScore: 80
    }
  },

  // PHASE 3: Policy & Procedure Development
  'module-3-1': {
    id: 'module-3-1',
    phaseId: 'phase-3',
    phaseName: 'Policy & Procedure Development',
    title: 'Policy Development & Management',
    duration: 40,
    difficulty: 'intermediate',
    learningObjectives: [
      'Develop effective compliance policies',
      'Establish policy governance and approval procedures',
      'Implement version control and change management',
      'Create sustainable policy review cycles'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Policy Framework & Design',
        duration: 18,
        content: `Building a comprehensive policy framework

POLICY HIERARCHY:
Level 1: Board-Approved Policies
- Enterprise-level policies
- Highest authority
- Require Board approval
- Examples: Code of Conduct, AML Policy, Sanctions Policy

Level 2: Management Policies
- Department or function-specific
- Approved by Executive Management
- Implement enterprise policies
- Examples: Customer Onboarding, Transaction Monitoring

Level 3: Procedures & Work Instructions
- Detailed step-by-step instructions
- Day-to-day operational guidance
- Approved by Department Heads
- Examples: New Account Procedures, Escalation Protocols

POLICY DESIGN PRINCIPLES:
✓ Clear Purpose & Scope
  - Why does this policy exist?
  - Who is covered?
  - What activities are addressed?

✓ Specific & Measurable
  - Use specific language, not vague concepts
  - Define terms clearly
  - Provide examples where appropriate

✓ Achievable
  - Realistic for organization to implement
  - Adequate resources available
  - Not overly burdensome

✓ Enforceable
  - Clear consequences for violations
  - Consistent application
  - Documented disciplinary process

COMMON POLICY AREAS:
1. AML/CFT Compliance
   - Customer identification and verification
   - Know Your Customer (KYC) procedures
   - Beneficial ownership identification
   - Ongoing customer monitoring

2. Sanctions Compliance
   - Sanctions screening procedures
   - Screening tools and vendors
   - Exception handling
   - Training requirements

3. Conflicts of Interest
   - Gifts and entertainment limits
   - Personal trading restrictions
   - Outside employment disclosure
   - Approval procedures

4. Code of Conduct
   - Ethical business practices
   - Confidentiality obligations
   - Insider trading restrictions
   - Reporting requirements

POLICY COMPONENTS:
1. Title & Version
2. Effective Date
3. Purpose & Scope
4. Roles & Responsibilities
5. Policy Statement
6. Procedures
7. Compliance Monitoring
8. Consequences for Non-Compliance
9. Approval Signatures
10. Review/Update Schedule`
      },
      {
        id: 'lesson-2',
        title: 'Version Control & Change Management',
        duration: 13,
        content: `Managing policy changes and versions

VERSION CONTROL SYSTEM:
Document the version history of all policies

Version 1.0 - Initial policy
Version 1.1 - Minor clarifications (no substantive changes)
Version 2.0 - Significant changes (new section added)
Version 2.1 - Clarifications after regulatory feedback

Numbering System:
- Major version (left of decimal): Substantive changes
- Minor version (right of decimal): Clarifications only

CHANGE MANAGEMENT PROCESS:
Step 1: Identify Need for Change (Weeks 1-2)
- Regulatory requirement change
- Business process change
- Feedback from staff or management
- Risk assessment findings

Step 2: Develop Proposed Changes (Weeks 2-3)
- Determine what needs to change
- Draft new policy language
- Impact analysis
- Stakeholder consultation

Step 3: Review & Approval (Weeks 3-4)
- Legal review for compliance
- Management review
- Executive approval
- Board approval (if required)

Step 4: Communication & Training (Weeks 4-6)
- Announce changes to staff
- Provide training on new requirements
- Distribute updated policies
- Answer questions

Step 5: Implementation (Ongoing)
- Implement new procedures
- Monitor for compliance
- Track effectiveness
- Document issues

TYPES OF CHANGES:
Substantive Changes (New Version)
- Addition of new requirements
- Modification of existing requirements
- Removal of outdated provisions
- Changes in roles/responsibilities
- Changes in procedures

Clarifying Changes (Minor Version)
- Grammar/spelling corrections
- Formatting improvements
- Clarification of existing language
- Examples added (no new requirements)
- Cross-reference updates

STAKEHOLDER COMMUNICATION:
Announcement Email
- Summary of changes
- Why changes are needed
- Implementation timeline
- Training schedule
- Q&A contact information

Training Materials
- What changed and why
- How to implement new procedures
- Q&A document
- Quick reference guides

Documentation
- Change log showing all versions
- Rationale for major changes
- Approval signatures and dates
- Implementation confirmation`
      },
      {
        id: 'lesson-3',
        title: 'Dissemination & Acknowledgment',
        duration: 9,
        content: `Ensuring employees know and comply with policies

DISSEMINATION METHODS:
1. Central Repository
   - Shared drive or internal website
   - Searchable by policy topic
   - Version control evident
   - Last updated date visible

2. Direct Communication
   - Email to all relevant staff
   - Announcement in staff meetings
   - Department-specific briefings
   - Management-led discussions

3. Training Programs
   - Formal training sessions
   - Online training modules
   - Department-specific training
   - Role-specific training

ACKNOWLEDGMENT & CERTIFICATION:
Documentation that employees received and understood policies

Acknowledgment Process:
1. Provide policy to employee
2. Employee reads policy
3. Employee certifies they read/understand
4. Save certification in personnel file
5. Escalate non-compliance

Methods:
- Signed acknowledgment form
- Online training completion certificate
- Attestation in HR system
- Email confirmation

RETENTION REQUIREMENTS:
- Maintain copies for audit trail
- Keep for 3-7 years (per regulation)
- Store in secure location
- Make accessible for investigations

ONGOING COMPLIANCE:
Regular Reminders
- Annual re-certification of key policies
- New hire orientation
- Annual training refresher
- Updates when policies change

Monitoring & Enforcement
- Track acknowledgment completion rates
- Escalate non-compliance
- Provide training to non-compliant staff
- Take disciplinary action if needed

TRACKING & REPORTING:
Policy Acknowledgment Report
- Who has acknowledged each policy
- Completion rates by department
- Outstanding acknowledgments
- Overdue acknowledgments

Management Dashboard
- Visual representation of compliance
- Trends over time
- Gaps by department
- Actions taken for non-compliance`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the highest level in a policy hierarchy?',
          options: [
            'Procedures and work instructions',
            'Management policies',
            'Board-approved policies',
            'Guidelines'
          ],
          correct: 2,
          explanation: 'Board-approved policies are at the highest level of authority in a policy hierarchy.'
        },
        {
          id: 'q2',
          question: 'What indicates a major version change to a policy?',
          options: [
            'Grammar correction (v1.0 to v1.1)',
            'Addition of new requirements (v1.0 to v2.0)',
            'Formatting improvement',
            'Cross-reference update'
          ],
          correct: 1,
          explanation: 'A major version change (left of decimal) indicates substantive changes like new requirements or procedures.'
        },
        {
          id: 'q3',
          question: 'What is the primary purpose of policy acknowledgment?',
          options: [
            'To reduce printing costs',
            'To provide documentation that employees received and understood policies',
            'To eliminate the need for training',
            'To reduce the number of violations'
          ],
          correct: 1,
          explanation: 'Policy acknowledgment provides documentation that employees received policies and understood requirements.'
        }
      ],
      passingScore: 80
    }
  },

  'module-3-2': {
    id: 'module-3-2',
    phaseId: 'phase-3',
    phaseName: 'Policy & Procedure Development',
    title: 'Procedure Documentation Best Practices',
    duration: 35,
    difficulty: 'intermediate',
    learningObjectives: [
      'Document procedures clearly and completely',
      'Create process flowcharts and work instructions',
      'Define roles and decision points',
      'Ensure procedure usability and compliance'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Writing Effective Procedures',
        duration: 12,
        content: `Creating clear, usable procedures that staff can follow

PROCEDURE DOCUMENTATION ELEMENTS:
1. Title
   - Clear, descriptive title
   - Identify function or process
   - Use consistent naming

2. Purpose
   - Why does this procedure exist?
   - What problem does it solve?
   - What is the business objective?

3. Scope
   - Who must follow this procedure?
   - When does it apply?
   - Applicable business units/departments
   - Geographic scope if relevant

4. Roles & Responsibilities
   - Who performs each step?
   - What authority do they need?
   - Approval authorities for exceptions
   - Escalation contacts

5. Step-by-Step Instructions
   - Number each step
   - Use clear, specific language
   - Define decision points
   - Include expected outcomes

6. Supporting Materials
   - Forms and templates
   - System navigation instructions
   - Account codes and GL mappings
   - Contact information

WRITING GUIDELINES:
✓ Use Active Voice
   - "Customer Service must verify the account"
   - NOT "The account verification will be done"

✓ Be Specific
   - "Complete within 2 business days"
   - NOT "Complete in a timely manner"

✓ Use Numbered Steps
   - Sequential, logical flow
   - Easy to reference
   - Clear progression

✓ Define Terms
   - Explain acronyms
   - Define role titles
   - Clarify business terms

✓ Include Examples
   - Show what a completed form looks like
   - Provide sample calculations
   - Illustrate correct vs. incorrect approach

✗ Avoid Jargon
   - Use simple, clear language
   - Explain complex concepts
   - Define technical terms

✗ Avoid Vague Language
   - "Appropriate" vs. "within limits defined in Policy X"
   - "As needed" vs. "monthly or when triggered by exception"

PROCEDURE FORMAT:
Procedure: [Name]
Version: [Version Number]
Effective Date: [Date]
Last Updated: [Date]
Process Owner: [Department/Role]

PURPOSE:
[Clear statement of why this procedure exists]

SCOPE:
[Who it applies to, when, and where]

ROLES & RESPONSIBILITIES:
Role | Responsibility
Customer Service | Verify customer identity
Compliance Officer | Review high-risk accounts
Manager | Approve exceptions

STEP-BY-STEP INSTRUCTIONS:
1. Customer provides identification documents
2. Verify documents are current and valid
3. Compare to government database
4. If match found, proceed to step 5
5. If no match, escalate to Compliance Officer
...and so on

REFERENCES:
[Links to related policies, procedures, forms]`
      },
      {
        id: 'lesson-2',
        title: 'Process Mapping & Flowcharts',
        duration: 13,
        content: `Visualizing processes to identify gaps and ensure clarity

FLOWCHART SYMBOLS:
Oval = Start/End point
Rectangle = Process step
Diamond = Decision point
Parallelogram = Input/Output
Arrow = Flow direction

CREATING A PROCESS MAP:
Step 1: Identify Process
- What is the process?
- Where does it start and end?
- What are the main steps?

Step 2: Gather Information
- Interview process owners
- Observe process in action
- Document current state
- Identify decision points

Step 3: Create Flowchart
- Use standard symbols
- Show sequential flow
- Identify decision branches
- Label all elements

Step 4: Validate
- Walk through with process owner
- Verify accuracy
- Identify gaps or errors
- Test with actual transactions

EXAMPLE: New Account Opening Process
Start
  ↓
Customer provides information
  ↓
Verify identity documents
  ↓
Decision: Documents valid?
  ├─ YES → Screen against watchlists
  │           ↓
  │         Decision: Match found?
  │           ├─ YES → Escalate to Compliance
  │           │         ↓
  │           │       Open account (flagged)
  │           └─ NO → Open account
  │
  └─ NO → Request additional documents
            ↓
          (return to verification step)
  ↓
End

IDENTIFYING GAPS:
Process Gaps (Steps Missing)
- Incomplete procedures
- Unclear decision criteria
- Missing approval steps
- No escalation procedure

Control Gaps (No Verification)
- No validation of data
- No approval required
- No documentation
- No monitoring

Exception Handling
- What happens if exception occurs?
- Who makes decision?
- When should it be escalated?
- How is it documented?

Single Points of Failure
- Only one person can perform step
- No backup if person unavailable
- No alternative path
- Creates bottleneck`
      },
      {
        id: 'lesson-3',
        title: 'Procedure Maintenance & Updates',
        duration: 10,
        content: `Keeping procedures current and effective

REVIEW & UPDATE CYCLE:
Annual Review
- Comprehensive review of all procedures
- Identify needed updates
- Incorporate regulatory changes
- Update based on lessons learned

Triggered Updates
- Regulatory change requires update
- Process or system change
- Business expansion or contraction
- Feedback from staff or audit

UPDATING PROCEDURES:
1. Identify What Changed
   - Process change?
   - System change?
   - Regulatory change?
   - New requirement?

2. Update Documentation
   - Update step-by-step instructions
   - Revise process map if needed
   - Update forms and templates
   - Revise approval authorities

3. Review & Approval
   - Process owner reviews
   - Compliance reviews for compliance changes
   - IT reviews for system changes
   - Management approves

4. Communication & Training
   - Notify affected staff
   - Provide training on changes
   - Distribute updated procedures
   - Confirm acknowledgment

DOCUMENT CONTROL:
Version Control
- Track all versions
- Date of each update
- Description of changes
- Who approved changes

Storage & Access
- Central repository
- Version-controlled
- Searchable and indexed
- Accessible to relevant staff

Archiving
- Retain old versions for audit trail
- Mark as superseded
- Keep for compliance period
- Make available for investigations

FEEDBACK & IMPROVEMENT:
Gathering Feedback
- Anonymous surveys
- Process owner interviews
- Compliance officer observations
- Exception analysis

Continuous Improvement
- Track procedure effectiveness
- Monitor exception rates
- Identify common issues
- Implement improvements
- Communicate changes`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the primary benefit of using flowcharts in procedures?',
          options: [
            'To make procedures look professional',
            'To visually identify gaps and ensure clarity of process flow',
            'To reduce the number of steps',
            'To eliminate the need for written instructions'
          ],
          correct: 1,
          explanation: 'Flowcharts visually represent processes, making gaps easier to identify and ensuring clarity of flow.'
        },
        {
          id: 'q2',
          question: 'What should you do when procedures need updating?',
          options: [
            'Update immediately without review',
            'Identify changes, update, review, get approval, communicate, and train',
            'Only update procedures annually',
            'Update procedures without notifying staff'
          ],
          correct: 1,
          explanation: 'Proper update procedures include identifying changes, updating documentation, getting approvals, communicating changes, and providing training.'
        }
      ],
      passingScore: 80
    }
  },

  'module-3-3': {
    id: 'module-3-3',
    phaseId: 'phase-3',
    phaseName: 'Policy & Procedure Development',
    title: 'Training Program Development',
    duration: 50,
    difficulty: 'intermediate',
    learningObjectives: [
      'Assess training needs across the organization',
      'Design effective compliance training programs',
      'Select appropriate training delivery methods',
      'Measure training effectiveness and knowledge retention'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Training Needs Assessment',
        duration: 18,
        content: `Determining what training is needed and who needs it

TRAINING CATEGORIES:
Mandatory Training (Everyone)
- Annual compliance training
- Code of Conduct
- Ethics training
- Anti-harassment training

Role-Specific Training
- AML training for Customer Service
- Sanctions training for Transaction team
- Reporting training for Finance
- Risk assessment training for managers

New Hire Training
- Company overview
- Compliance program
- Relevant policies and procedures
- System training

Regulatory Training
- New regulation requirement
- Changes to existing regulations
- Regulatory examination feedback
- Specialized certifications

TRAINING NEEDS ASSESSMENT PROCESS:
1. Identify Training Requirements
   - Regulatory requirements
   - Policy requirements
   - Risk-based needs
   - Stakeholder input

2. Map Training to Roles
   - What is each role responsible for?
   - What do they need to know?
   - How critical is compliance in role?
   - What is risk of non-compliance?

3. Assess Current Knowledge
   - Survey staff on understanding
   - Test current knowledge
   - Identify gaps
   - Assess retention of past training

4. Prioritize Training
   - Critical compliance areas (High priority)
   - Important areas (Medium priority)
   - Helpful knowledge (Low priority)
   - Allocate resources accordingly

5. Determine Training Method
   - In-person classroom
   - Online self-paced
   - Webinar/virtual classroom
   - One-on-one coaching
   - On-the-job training

SAMPLE TRAINING MATRIX:
Role | Required Training | Frequency | Method
Customer Service | KYC, AML | Annual | Online + Role-Specific
Compliance Officer | AML, Sanctions, Monitoring | Annual + As needed | Online + In-person
Executive Management | Compliance Overview, Governance | Annual | In-person
Front-line Staff | Code of Conduct, Reporting | Annual | Online

ASSESSING KNOWLEDGE GAPS:
Pre-Assessment Test
- Test current knowledge before training
- Identify specific gaps
- Tailor training to needs
- Baseline for measuring improvement

Post-Assessment Test
- Test knowledge after training
- Measure learning effectiveness
- Identify who needs additional training
- Document completion

TRAINING DOCUMENTATION:
Training Record Should Include:
- Employee name and role
- Training topic
- Date completed
- Duration/hours
- Format (online, classroom, etc.)
- Test score (if applicable)
- Completion certificate`
      },
      {
        id: 'lesson-2',
        title: 'Training Design & Delivery',
        duration: 20,
        content: `Creating and delivering effective training programs

TRAINING DESIGN PRINCIPLES:
✓ Learning Objectives
  - What should participants know after training?
  - What should they be able to do?
  - Make objectives measurable

✓ Relevant Content
  - Include realistic scenarios
  - Use actual policies and procedures
  - Provide examples from organization
  - Show how it applies to daily work

✓ Engaging Format
  - Vary presentation methods
  - Include interactive elements
  - Use multimedia where appropriate
  - Keep sessions focused and concise

✓ Clear Instructions
  - Explain why training matters
  - Show how to apply learning
  - Provide job aids/reference materials
  - Allow questions and discussion

TRAINING METHODS:
In-Person Classroom Training
- Advantages: Interactive, Q&A, discussion, relationship building
- Disadvantages: Expensive, scheduling challenges, travel
- Best for: Complex topics, group discussion, high-risk areas

Online Self-Paced Training
- Advantages: Flexible, scalable, consistent delivery, cost-effective
- Disadvantages: Low engagement, no discussion, self-motivation required
- Best for: General awareness, large groups, scheduling flexibility

Virtual Classroom/Webinar
- Advantages: Interactive, remote, recorded for later viewing
- Disadvantages: Technology issues, engagement challenges
- Best for: Geographic dispersion, timely updates, expert speakers

One-on-One Training
- Advantages: Personalized, immediate Q&A, hands-on
- Disadvantages: Expensive, time-consuming, inconsistent delivery
- Best for: Struggling learners, complex skills, sensitive topics

On-the-Job Training
- Advantages: Real-world context, immediate application, mentoring
- Disadvantages: Depends on trainer quality, inconsistent
- Best for: Procedures, system training, practical skills

TRAINING CONTENT:
Module Structure
1. Introduction & Importance (5 min)
   - Why this topic matters
   - How it applies to your role
   - What you'll learn

2. Core Content (15-20 min)
   - Key concepts
   - Policies and procedures
   - Examples and scenarios
   - Do's and Don'ts

3. Interactive Element (10 min)
   - Scenario discussion
   - Role-play exercise
   - Case study analysis
   - Q&A

4. Reinforcement (5 min)
   - Key takeaways
   - Job aids provided
   - Questions answered
   - Confirmation of understanding

DELIVERY CONSIDERATIONS:
Timing
- Schedule when most staff available
- Avoid peak business times
- Consider time zones if remote
- Allow flexibility for makeup training

Audience Size
- Large groups (100+): Webinar or recorded session
- Medium groups (20-50): Virtual classroom or large classroom
- Small groups (5-15): In-person or virtual
- Individual: One-on-one or online

Accessibility
- Provide materials in advance
- Accommodate learning disabilities
- Provide ASL interpreter if needed
- Record sessions for later viewing`
      },
      {
        id: 'lesson-3',
        title: 'Measuring Training Effectiveness',
        duration: 12,
        content: `Assessing whether training achieves its goals

TRAINING EFFECTIVENESS METRICS:
1. Completion Rate
   - % of required staff who completed training
   - Formula: Completed / Required × 100%
   - Target: 90-100%
   - Red flag: <80%

2. Knowledge Gain
   - Pre vs. post assessment scores
   - Formula: (Post score - Pre score) / Pre score × 100%
   - Target: 20%+ improvement
   - Indicates learning occurred

3. Test Pass Rate
   - % of participants passing knowledge test
   - Target: 80%+ passing
   - Red flag: <70%
   - Identifies need for remedial training

4. Retention Rate
   - % still demonstrating knowledge 3-6 months later
   - Measured via: Test, observation, behavior
   - Target: 70%+
   - Indicates lasting learning

5. Behavioral Change
   - Do staff apply learning in their work?
   - Measured via: Exception rates, control testing, observations
   - Target: Reduced violations, improved control execution
   - Real-world impact of training

EVALUATION METHODS:
Level 1: Reaction
- Did participants like the training?
- Survey questions: Relevance, presenter, materials, pacing
- Easy to measure, limited insight
- Useful for improving training delivery

Level 2: Learning
- Did participants learn the material?
- Knowledge assessment tests
- Measures understanding
- Doesn't guarantee on-the-job application

Level 3: Behavior
- Do participants apply learning?
- Observation, control testing, exception monitoring
- Measures real-world impact
- Most important level

Level 4: Impact
- Did training achieve business objectives?
- Regulatory compliance, reduced violations, better controls
- Measures organizational benefit
- Hardest to isolate

ANALYZING RESULTS:
Identifying Gaps
- Who is not passing tests?
- Which topics have low comprehension?
- Are certain departments underperforming?
- Patterns by role or tenure?

Root Cause Analysis
- Why are results below target?
- Is training material unclear?
- Is topic inherently complex?
- Are learners unmotivated?
- Is training method ineffective?

Improvement Actions
- Revise training content
- Provide remedial training
- Change delivery method
- Provide additional practice
- One-on-one coaching for struggling learners

DOCUMENTATION & REPORTING:
Training Report
- Completion rates by department
- Assessment scores and trends
- Participants who need remediation
- Training effectiveness metrics
- Recommendations for improvements

Management Dashboard
- Visual representation of training status
- Completion % over time
- Assessment scores by topic
- Departmental comparisons
- Compliance status`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the first step in training needs assessment?',
          options: [
            'Design the training materials',
            'Identify training requirements from regulations and policies',
            'Schedule training sessions',
            'Select training vendors'
          ],
          correct: 1,
          explanation: 'Identifying training requirements is the first step to ensure training is targeted and appropriate.'
        },
        {
          id: 'q2',
          question: 'Which training method is best for complex topics requiring discussion?',
          options: [
            'Online self-paced training',
            'Email with attachment',
            'In-person classroom training',
            'Recorded video only'
          ],
          correct: 2,
          explanation: 'In-person classroom training allows for interactive discussion, Q&A, and group problem-solving.'
        },
        {
          id: 'q3',
          question: 'Which level of evaluation measures real-world application of training?',
          options: [
            'Level 1: Reaction',
            'Level 2: Learning',
            'Level 3: Behavior',
            'Level 4: Impact'
          ],
          correct: 2,
          explanation: 'Level 3 (Behavior) measures whether participants actually apply the training in their daily work.'
        }
      ],
      passingScore: 80
    }
  },

  // PHASE 4: Implementation & Deployment
  'module-4-1': {
    id: 'module-4-1',
    phaseId: 'phase-4',
    phaseName: 'Implementation & Deployment',
    title: 'Change Management Excellence',
    duration: 50,
    difficulty: 'advanced',
    learningObjectives: [
      'Manage organizational change effectively',
      'Build stakeholder buy-in and engagement',
      'Address resistance and manage adoption',
      'Sustain changes through transition'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Change Management Framework',
        duration: 18,
        content: `Understanding the phases of organizational change

CHANGE MANAGEMENT PHASES:
Phase 1: Awareness (Weeks 1-4)
- Communicate why change is needed
- Explain what will change
- Describe the vision for future state
- Address concerns and questions
- Objective: Build understanding

Phase 2: Acceptance (Weeks 4-8)
- Provide detailed training
- Show how to use new processes
- Demonstrate benefits
- Celebrate early wins
- Objective: Overcome resistance

Phase 3: Adoption (Weeks 8-12)
- Support full implementation
- Troubleshoot issues
- Reinforce training
- Monitor compliance
- Objective: Embed new behaviors

Phase 4: Anchoring (Weeks 12+)
- Integrate into normal operations
- Reinforce through incentives
- Measure and celebrate results
- Plan for continuous improvement
- Objective: Sustain changes

CHANGE CURVE (Emotional Adoption):
Shock/Denial
  ↓
Anger/Resistance
  ↓
Negotiation/Adjustment
  ↓
Depression/Acceptance
  ↓
Integration/Adaptation

STAKEHOLDER ANALYSIS:
Champions (10-15%)
- Strongly support change
- Energize others
- Trusted messengers
- Use them to influence others

Supporters (20-30%)
- Generally positive
- Need clear benefits
- Will help if engaged
- Provide training support

Neutral (40-50%)
- Wait and see approach
- Can be influenced either way
- Need to see success
- Critical mass to convert

Resisters (10-20%)
- Oppose change
- Fear the unknown
- Concerned about impact
- Need extra support

RESISTANCE SOURCES:
Fear of Unknown
- "How will this affect my job?"
- Address through information and training

Loss of Control
- "I won't know what I'm doing"
- Address through involvement and skills

Loss of Status/Power
- "My expertise becomes obsolete"
- Address through recognition and new opportunities

Workload Concerns
- "I'm already too busy"
- Address through resource allocation

CHANGE LEADERSHIP:
Visible Executive Support
- Leadership demonstrates commitment
- Executives use new processes
- Executives communicate importance
- Budget allocated to support change

Clear Vision
- What will be different after change?
- What will improve?
- How does this help us?
- Compelling reason to change

Consistent Messaging
- Same message from all leaders
- Reinforce through multiple channels
- Address concerns openly
- Show alignment across organization

Persistent Attention
- Continue focus despite challenges
- Allocate time and resources
- Celebrate progress
- Don't move on too quickly`
      },
      {
        id: 'lesson-2',
        title: 'Communication & Engagement Strategy',
        duration: 18,
        content: `Keeping stakeholders informed and engaged throughout change

COMMUNICATION STRATEGY:
Frequency
- Weeks 1-4: Weekly communications
- Weeks 4-12: Bi-weekly communications
- Weeks 12+: Monthly communications

Channels
- Leadership messages from CEO
- Department/team meetings
- Email announcements
- Intranet updates
- All-hands meetings
- One-on-one conversations

Messengers
- CEO/Executive leaders
- Department heads
- Project managers
- Training team
- Peer champions

COMMUNICATION CONTENT:
What We're Changing
- Specific processes that will change
- Timeline for changes
- Why we're making changes
- Benefits for organization and individuals

How to Prepare
- Training schedule and registration
- Resources available
- Questions answered
- Support offered

How to Succeed
- Step-by-step instructions
- Tips and best practices
- Common mistakes to avoid
- Success stories from early adopters

How You're Doing
- Progress towards goals
- Celebration of milestones
- Challenges being addressed
- Next steps

ENGAGEMENT TACTICS:
Involve Early Adopters
- Pilot with volunteers
- Get feedback and refinement
- Create peer advocates
- Share their success stories

Create Quick Wins
- Celebrate early successes
- Show immediate benefits
- Build momentum
- Demonstrate commitment

Provide Support
- Training sessions
- Help desk for questions
- Job aids and documentation
- Peer mentors
- One-on-one coaching

Address Concerns
- Open forum for questions
- Anonymous feedback options
- Rapid response to issues
- Transparent problem-solving

MANAGING RESISTANCE:
Listen & Understand
- Why are they resisting?
- What are their concerns?
- What would make it work?
- What support do they need?

Acknowledge Concerns
- "I understand your concern"
- Validate their perspective
- Avoid being defensive
- Show respect

Provide Information
- Answer their specific questions
- Provide evidence and data
- Show how it works
- Introduce them to champion users

Offer Support
- Extra training
- One-on-one coaching
- Extended timeline
- Personal attention`
      },
      {
        id: 'lesson-3',
        title: 'Implementation Tracking & Support',
        duration: 14,
        content: `Monitoring progress and providing support during implementation

MONITORING PROGRESS:
Adoption Metrics
- % of staff trained
- % actively using new processes
- Time to proficiency
- Error/exception rates
- User feedback scores

Timeline Milestones
- Week 2: 50% trained
- Week 4: 75% trained
- Week 8: 95% trained
- Week 12: Business as usual

Red Flags
- Training completion falling behind
- High exception rates
- Negative user feedback
- Staff reverting to old processes
- Leadership commitment wavering

SUPPORT INFRASTRUCTURE:
Help Desk
- Dedicated phone line or email
- Respond within 2 hours
- Document and track issues
- Escalate complex problems
- Provide training for common issues

Training Support
- Repeat training sessions
- One-on-one coaching
- Group practice sessions
- Extended office hours
- Online resources and videos

Documentation
- Step-by-step quick guides
- FAQ document
- System navigation screenshots
- Example transactions
- Troubleshooting guide

Issue Resolution Process:
1. User reports issue
2. Help desk captures details
3. Determine root cause
4. Implement fix or workaround
5. Communicate solution
6. Track trend for pattern detection
7. Update documentation if needed

SUSTAINING CHANGE:
Reinforcement & Incentives
- Recognition for early adopters
- Incentives for compliance
- Performance metrics tied to change
- Include in performance evaluations

Continuous Improvement
- Gather feedback on what's working
- Identify areas for refinement
- Make adjustments
- Communicate improvements

Integration into Normal Operations
- Incorporate into standard procedures
- Update job descriptions if needed
- Include in onboarding training
- Make it "the way we do business"

Persistence Through Resistance
- Expect regression to old ways
- Reinforce new approaches
- Address reversions quickly
- Celebrate sustained adoption
- Don't move forward until embedded`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the primary objective of the Awareness phase?',
          options: [
            'To implement the new system immediately',
            'To build understanding of why change is needed',
            'To complete all training',
            'To measure success'
          ],
          correct: 1,
          explanation: 'The Awareness phase focuses on communicating why change is needed and building understanding.'
        },
        {
          id: 'q2',
          question: 'Which stakeholder group should be used as peer advocates?',
          options: [
            'The resisters',
            'The champions and supporters',
            'Only executives',
            'External consultants'
          ],
          correct: 1,
          explanation: 'Champions and early adopters are trusted messengers who can influence others and share success.'
        }
      ],
      passingScore: 80
    }
  },

  'module-4-2': {
    id: 'module-4-2',
    phaseId: 'phase-4',
    phaseName: 'Implementation & Deployment',
    title: 'System Implementation & Technology',
    duration: 55,
    difficulty: 'advanced',
    learningObjectives: [
      'Select and configure compliance systems',
      'Integrate with existing infrastructure',
      'Conduct thorough system testing',
      'Manage go-live and support transition'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'System Selection & Configuration',
        duration: 20,
        content: `Choosing the right technology and configuring it for your needs

SYSTEM EVALUATION:
Functional Requirements
- What features do you need?
- Customer screening capability
- Transaction monitoring
- Reporting and analytics
- Workflow and escalation
- Integration with banking platforms

Technical Requirements
- Deployment options (cloud vs. on-premise)
- Security and data protection
- Scalability and performance
- Supported integrations
- Browser and device support
- API capabilities

Vendor Evaluation
- Financial stability and longevity
- Customer references and reviews
- Implementation experience
- Support and service levels
- Training and documentation
- Pricing and licensing model

IMPLEMENTATION APPROACH:
Phase 1: Configuration (Weeks 1-4)
- Customize system for your workflows
- Configure user roles and permissions
- Define watchlists and screening parameters
- Set up reporting and dashboards
- Create user accounts and access

Phase 2: Integration (Weeks 3-6)
- Connect to customer database
- Connect to transaction systems
- Configure data feeds
- Test data flow
- Reconcile data sources

Phase 3: Testing (Weeks 5-8)
- Unit testing (individual features)
- Integration testing (systems working together)
- User acceptance testing (end users verify functionality)
- Performance testing (handles volume)
- Security testing (data protection)

Phase 4: Go-Live (Week 8-9)
- Train users
- Parallel run if needed (old and new systems)
- Transition transactions
- Monitor closely
- Support users

CONFIGURATION DECISIONS:
Screening Parameters
- Which watchlists to use?
- Matching threshold (exact vs. similar names)
- Refresh frequency
- How to handle matches?
- Exception thresholds

Monitoring Rules
- What makes a suspicious transaction?
- Volume thresholds
- Velocity thresholds
- Unusual patterns
- Alert fatigue management

Escalation Workflow
- When to escalate automatically?
- Who reviews what?
- Approval authorities
- Timeline for decisions
- Documentation requirements

VENDOR MANAGEMENT:
Service Level Agreement (SLA)
- System uptime guarantee
- Support response time
- Bug fix timelines
- Security patch procedures
- Data backup and recovery

Support Options
- Phone support hours
- Email support
- Help desk ticketing
- On-site support if needed
- Training resources

Change Management
- How are system updates managed?
- Testing before production
- Notification of changes
- Backward compatibility
- Rollback procedures`
      },
      {
        id: 'lesson-2',
        title: 'Testing & Quality Assurance',
        duration: 18,
        content: `Ensuring system works correctly before go-live

TESTING APPROACH:
Unit Testing
- Test each feature individually
- Verify functionality works as designed
- Test edge cases and exceptions
- Identify and fix bugs

Integration Testing
- Test features working together
- Verify data flows correctly
- Test with realistic data volumes
- Identify integration issues

User Acceptance Testing (UAT)
- End users test the system
- Verify it meets their needs
- Test real-world scenarios
- Identify usability issues
- Sign-off when acceptable

Performance Testing
- Test with production-level data volumes
- Verify response times acceptable
- Identify system limits
- Test peak usage scenarios
- Optimize as needed

Security Testing
- Verify authentication works
- Test authorization/permissions
- Verify data encryption
- Test for vulnerabilities
- Validate compliance controls

TEST PLANNING:
Test Cases
- Define what to test
- Expected results
- Pass/fail criteria
- Who conducts test
- When test is performed

Test Data
- Representative sample
- Edge cases
- Large data sets
- Historical data
- Realistic combinations

Test Schedule
- Unit testing: Weeks 5-6
- Integration testing: Weeks 6-7
- UAT: Weeks 7-8
- Performance testing: Week 7
- Security testing: Ongoing

BUG TRACKING:
Severity Levels
- Critical: System down, data loss risk
- High: Major functionality broken
- Medium: Feature doesn't work fully
- Low: Minor issue, workaround available

Priority Levels
- Fix before go-live
- Fix within 2 weeks
- Fix in next update
- Enhancement for future

BUG RESOLUTION:
1. Document the bug (symptoms, steps to reproduce)
2. Classify severity and priority
3. Assign to developer
4. Developer fixes
5. Test verifies fix
6. Document resolution
7. Release to production

CUTOVER PLANNING:
Parallel Run Option
- Run old and new systems together
- Verify results match
- Build confidence in new system
- Option to rollback if needed
- Time-intensive but low-risk

Direct Cutover Option
- Stop old system, start new system
- Faster transition
- Higher risk if issues occur
- Requires thorough testing
- Have rollback plan ready

Rollback Plan
- Can we go back to old system?
- How long is rollback possible?
- What data needs to be restored?
- Who makes rollback decision?
- How is decision communicated?`
      },
      {
        id: 'lesson-3',
        title: 'Go-Live & Ongoing Support',
        duration: 17,
        content: `Managing the transition to production and supporting users

GO-LIVE CHECKLIST:
Pre-Go-Live (48 hours before)
- All testing complete and signed off
- Backups created
- Support team briefed and ready
- Communication sent to users
- System validated one more time
- Rollback plan documented
- Leadership briefed on status

Go-Live Day
- Start time confirmed
- Monitoring team ready
- Support team staffed
- User questions anticipated
- Executive sponsor available
- Regular status updates

Post-Go-Live Monitoring:
First 24 Hours (Critical)
- Monitor system performance
- Watch for errors and exceptions
- Respond to user questions immediately
- Document issues
- Check data accuracy
- Be ready to rollback if critical issues

First Week
- Monitor for patterns of issues
- Track error and exception rates
- Gather user feedback
- Fine-tune system parameters
- Conduct daily check-ins
- Maintain aggressive support

First Month
- Continue performance monitoring
- Assess operational readiness
- User adoption tracking
- Identify process improvements
- Plan for any needed enhancements
- Transition to normal support

SUPPORT STRUCTURE:
24/7 Monitoring Team (First Week)
- System engineers monitoring
- On-call escalation
- Can make emergency changes
- In contact with vendor
- Authority to rollback if needed

Help Desk Team
- Answer user questions
- Log issues and problems
- Provide workarounds
- Document solutions
- Escalate to technical team

Training Team
- Provide additional training
- Help struggling users
- Conduct refresher sessions
- Create additional job aids
- One-on-one coaching

ISSUE MANAGEMENT:
User Reports Issue
↓
Help Desk logs in tracking system
↓
Severity/Priority determined
↓
If critical: Escalate immediately
↓
Technical team investigates
↓
Solution/workaround provided
↓
User confirms resolution
↓
Issue documented and closed
↓
Check for patterns/trend

SUCCESS METRICS:
System Uptime
- Target: 99%+ uptime
- Monitor 24/7
- Log all downtime
- Investigate causes

User Adoption
- % of users trained
- % actively using system
- Time to proficiency
- Error/exception rates
- User satisfaction scores

Data Quality
- Transactions processed: 100%
- Match rates accurate
- False positive rate acceptable
- Data completeness verified
- Results trusted by users

TRANSITION TO STEADY STATE:
Reduce Monitoring
- Gradually reduce monitoring
- Transition to normal support levels
- Monitor for regression to old processes
- Celebrate achievement

Stabilize Processes
- Workflows documented
- Standard procedures established
- Users competent and confident
- System running smoothly
- Known issues documented

Continuous Improvement
- Gather ongoing feedback
- Plan enhancements
- Optimize performance
- Update training materials
- Share lessons learned`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'Which testing type involves end users verifying the system meets their needs?',
          options: [
            'Unit testing',
            'Integration testing',
            'User Acceptance Testing (UAT)',
            'Performance testing'
          ],
          correct: 2,
          explanation: 'User Acceptance Testing (UAT) involves end users testing real-world scenarios and signing off on functionality.'
        },
        {
          id: 'q2',
          question: 'What is an advantage of parallel run during cutover?',
          options: [
            'It is faster than direct cutover',
            'It verifies results match and allows rollback',
            'It requires less testing',
            'It reduces support costs'
          ],
          correct: 1,
          explanation: 'Parallel run allows comparison of old and new system results and provides an option to rollback if issues occur.'
        }
      ],
      passingScore: 80
    }
  },

  // PHASE 5: Monitoring, Testing & Continuous Improvement
  'module-5-1': {
    id: 'module-5-1',
    phaseId: 'phase-5',
    phaseName: 'Monitoring, Testing & Continuous Improvement',
    title: 'Monitoring & Surveillance Best Practices',
    duration: 50,
    difficulty: 'advanced',
    learningObjectives: [
      'Implement effective daily monitoring procedures',
      'Use automated monitoring tools effectively',
      'Detect and escalate compliance issues',
      'Manage alert volumes and reduce false positives'
    ],
    lessons: [
      {
        id: 'lesson-1',
        title: 'Daily Monitoring Procedures',
        duration: 18,
        content: `Establishing routine monitoring to detect compliance issues early

MONITORING FRAMEWORK:
Real-Time Monitoring
- Continuous transaction review
- System-generated alerts
- Automated rule application
- Immediate escalation of critical issues
- 24/7 coverage

Daily Monitoring
- Morning review of overnight activity
- Exception report analysis
- Alert investigation and disposition
- Escalation of unresolved issues
- Documentation and tracking

Weekly Monitoring
- Trend analysis of daily activity
- Exception pattern review
- Control effectiveness assessment
- Staffing and resource adequacy
- Emerging risk identification

Monthly Monitoring
- Comprehensive compliance report
- KRI assessment and trending
- Control testing results
- Regulatory correspondence review
- Program effectiveness evaluation

MONITORING ACTIVITIES:
Transaction Monitoring
- Review flagged transactions
- Determine if legitimate or suspicious
- Escalate suspicious activity
- Document disposition
- Track patterns

Customer Monitoring
- Review customer activity over time
- Identify unusual patterns
- Monitor high-risk customers closely
- Update customer risk ratings
- Escalate when patterns change

Alert Investigation
- System generates alert
- Analyst reviews alert
- Determines if true positive or false positive
- Escalates if violation indicated
- Documents decision

Issue Escalation
- Minor issues: Log and monitor
- Significant issues: Notify business unit head
- Critical issues: Notify CCO/Executive immediately
- Regulatory issues: Notify General Counsel
- Document escalation and resolution

MONITORING SYSTEMS:
Compliance Software Features
- Automated transaction screening
- Customer due diligence tracking
- Watchlist management
- Alert generation and workflow
- Reporting and analytics
- Audit trail and documentation

Configuration
- Define screening rules
- Set alert thresholds
- Establish escalation procedures
- Configure reports
- Set system parameters

Reports Generated
- Daily transaction monitoring report
- Daily exceptions and alerts
- Weekly trend analysis
- Monthly compliance report
- Ad-hoc compliance reports

STAFFING CONSIDERATIONS:
Monitoring Team Size
- Depends on transaction volume
- Depends on customer base
- Depends on risk profile
- Depends on alert volume
- Depends on automation level

Shift Coverage
- Day shift: Primary monitoring staff
- Evening shift: Reduced staff or outsourced
- Night/Weekend: Minimal or outsourced
- On-call escalation: For critical issues
- Backup plans for absences

MANAGING ALERT VOLUME:
Alert Fatigue
- Too many false positives
- Staff become desensitized
- True issues missed
- Low effectiveness
- High costs

Reducing False Positives
- Tune system rules
- Increase matching thresholds
- Use customer profiles
- Exclude low-risk patterns
- Continuous refinement

Alert Management Strategy
- Critical alerts: Investigate immediately
- High priority: Investigate same day
- Medium priority: Investigate within 2-3 days
- Low priority: Batch investigation
- Training-related: Regular review`
      },
      {
        id: 'lesson-2',
        title: 'Control Testing & Validation',
        duration: 18,
        content: `Systematic testing to ensure controls work effectively

CONTROL TESTING APPROACH:
Test Planning
- Which controls to test?
- When to test?
- What sample size?
- How frequently?
- Who conducts test?

Test Execution
- Gather sample population
- Apply control to sample
- Compare results
- Document findings
- Calculate effectiveness rate

Results Analysis
- What % passed control?
- What issues identified?
- Root cause analysis
- Trend analysis
- Severity assessment

TESTING METHODOLOGIES:
Sample Testing (Most Common)
- Select representative sample
- Test the sample against control
- Extrapolate results to population
- Efficient and practical
- Trade-off: May not catch all issues

Full Population Testing
- Test every transaction/item
- 100% certainty
- Very time-intensive
- High cost
- Used for highest-risk items

Continuous Testing
- Automated testing built into system
- Run every transaction/item
- No sampling required
- Most effective
- Requires system capability

DATA SELECTION:
Sample Size Considerations
- Larger sample = more confidence
- Statistical guidance: 30-50 items minimum
- High-risk areas: Full population
- Low-risk areas: Smaller samples
- Quarterly trending: Same size for comparison

Sample Period Selection
- Recent period (current month/quarter)
- Includes peak and normal volumes
- Captures seasonal variations
- Sufficient time has passed to identify issues
- Remediation can still occur

TESTING DOCUMENTATION:
Test Protocol (Before Testing)
- Control ID and name
- Test objective
- Test population
- Sample size and selection method
- Expected results
- Pass/fail criteria
- Responsible person
- Test date

Test Results (After Testing)
- Number tested and results
- % compliance rate
- Issues identified
- Root cause of failures
- Severity assessment
- Remediation required
- Follow-up testing date

FOLLOW-UP & REMEDIATION:
Issue Severity Assessment
- Critical (>5% failure): Immediate action
- Significant (2-5% failure): Action within 30 days
- Minor (<2% failure): Monitor and plan

Remediation Actions
- Control design improvement
- Training for staff
- Process adjustment
- System fix or configuration
- Increased monitoring

Remediation Verification
- Re-test after remediation
- Confirm issue is resolved
- Document resolution
- Close out issue
- Update control documentation`
      },
      {
        id: 'lesson-3',
        title: 'Compliance Reporting & Analytics',
        duration: 14,
        content: `Creating reports and using data to improve compliance

REPORTING FRAMEWORK:
Audience & Frequency
- Board: Quarterly
- Executive Management: Monthly
- Compliance Management: Weekly
- Business Units: As needed
- Regulators: As required

Report Types
- Executive Summary: High-level overview
- Detailed Report: Full compliance analysis
- Ad-Hoc Reports: Specific issues
- Trend Reports: Performance over time
- Risk Reports: Emerging risks

EXECUTIVE SUMMARY REPORT:
Compliance Status
- Overall assessment: Green/Yellow/Red
- Key metrics: Positive trends
- Major issues: Requiring attention
- Resource needs: Budget and staffing
- Top priorities: Next steps

Detailed Findings
- KRI performance and trends
- Control testing results
- Monitoring findings
- Notable transactions escalated
- Regulatory correspondence

MANAGEMENT DASHBOARD:
Key Metrics Displayed
- Compliance program health (Green/Yellow/Red)
- KRIs vs. thresholds
- Exception/violation counts
- Training completion rates
- Control test results
- Monitoring alert volume

Visual Representation
- Trending graphs
- Heat maps (risk by area)
- Status indicators
- YTD comparison
- Drill-down capability

DATA ANALYSIS:
Trend Analysis
- Compare current to previous periods
- Identify increasing/decreasing trends
- Project future performance
- Assess control effectiveness
- Identify emerging issues

Root Cause Analysis
- Why did violations occur?
- Systemic vs. isolated issue?
- Are controls effective?
- Is training adequate?
- Does process need change?

Benchmarking
- Compare to industry standards
- Compare to peer organizations
- Compare to own historical performance
- Identify best practices
- Set realistic improvement targets

CONTINUOUS IMPROVEMENT:
Feedback Loops
- Compliance staff feedback
- Business unit input
- Regulatory examination findings
- Staff survey results
- Customer complaints

Improvement Planning
- Identify improvement opportunities
- Prioritize based on impact/effort
- Assign responsibility
- Set timeline
- Communicate plans

Implementation & Monitoring
- Execute improvement plan
- Track progress
- Measure effectiveness
- Celebrate wins
- Document lessons learned

REPORTING BEST PRACTICES:
✓ Objective & Data-Driven
  - Use actual data, not opinions
  - Present facts clearly
  - Support conclusions with evidence

✓ Timely & Relevant
  - Deliver on schedule
  - Focus on current issues
  - Include actionable recommendations

✓ Clear & Accessible
  - Avoid jargon
  - Use charts and graphs
  - Write for the audience
  - Executive summary first

✓ Transparent & Honest
  - Report both positives and negatives
  - Don't sugarcoat issues
  - Show impact of non-compliance
  - Recommend resources needed`
      }
    ],
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is alert fatigue and why is it a problem?',
          options: [
            'Staff tiredness from working long hours',
            'Too many false positive alerts causing staff to become desensitized',
            'System crashes from too many alerts',
            'Not enough alerts being generated'
          ],
          correct: 1,
          explanation: 'Alert fatigue occurs when too many false positives overwhelm staff, causing them to miss real issues.'
        },
        {
          id: 'q2',
          question: 'What is the advantage of continuous testing over sample testing?',
          options: [
            'Continuous testing is less expensive',
            'Continuous testing tests every transaction, not just a sample',
            'Continuous testing requires no documentation',
            'Continuous testing is faster to implement'
          ],
          correct: 1,
          explanation: 'Continuous testing applies controls to every transaction automatically, providing complete coverage without sampling risk.'
        }
      ],
      passingScore: 80
    }
  }
};

export default trainingModules;
